'use client';

import React, { useState } from 'react';
import {
  ExtendedArchiMateDiagram,
  PerformanceMetadata,
} from '@/lib/archimate/extended-types';
import {
  calculatePerformanceScore,
  calculateDiagramPerformanceScore,
  detectBottlenecks,
  getMissingSLOs,
  generatePerformanceReport,
  exportPerformanceToCSV,
  getPerformanceColor,
} from '@/lib/archimate/performance-utils';
import { X, Activity, AlertTriangle, Download, Zap } from 'lucide-react';
import { PanelHelpDialog, HelpIconButton } from './PanelHelpDialog';

interface PerformancePanelProps {
  diagram: ExtendedArchiMateDiagram;
  selectedElementId?: string;
  onUpdate: (diagram: ExtendedArchiMateDiagram) => void;
  onClose: () => void;
  onToggleHeatmap: (enabled: boolean) => void;
}

export default function PerformancePanel({
  diagram,
  selectedElementId,
  onUpdate,
  onClose,
  onToggleHeatmap,
}: PerformancePanelProps) {
  const [viewMode, setViewMode] = useState<'overview' | 'element'>('overview');
  const [showHeatmap, setShowHeatmap] = useState(false);

  const [elementPerf, setElementPerf] = useState<PerformanceMetadata>({
    slo: {},
    scalability: { min: 1, max: 10, autoScale: false },
    bottleneck: false,
  });
  const [showHelp, setShowHelp] = useState(false);

  const overallScore = calculateDiagramPerformanceScore(diagram);
  const bottlenecks = detectBottlenecks(diagram);
  const missingSLOs = getMissingSLOs(diagram);

  const handleSave = () => {
    if (!selectedElementId) return;

    const updatedDiagram = {
      ...diagram,
      elementMetadata: {
        ...diagram.elementMetadata,
        [selectedElementId]: {
          ...diagram.elementMetadata[selectedElementId],
          performance: elementPerf,
        },
      },
    };

    onUpdate(updatedDiagram);
  };

  const handleToggleHeatmap = () => {
    const newState = !showHeatmap;
    setShowHeatmap(newState);
    onToggleHeatmap(newState);
  };

  const handleExportReport = () => {
    const report = generatePerformanceReport(diagram);
    const blob = new Blob([report], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${diagram.name}-performance-report.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const csv = exportPerformanceToCSV(diagram);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${diagram.name}-performance.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Load element performance if selected
  React.useEffect(() => {
    if (selectedElementId) {
      const metadata = diagram.elementMetadata[selectedElementId];
      if (metadata?.performance) {
        setElementPerf(metadata.performance);
      } else {
        setElementPerf({
          slo: {},
          scalability: { min: 1, max: 10, autoScale: false },
          bottleneck: false,
        });
      }
      setViewMode('element');
    }
  }, [selectedElementId, diagram.elementMetadata]);

  const selectedElement = selectedElementId
    ? diagram.elements.find((e) => e.id === selectedElementId)
    : null;

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    if (score >= 40) return 'text-accent';
    return 'text-red-500';
  };

  return (
    <div className="fixed right-0 top-0 h-screen w-[550px] bg-background border-l border-border flex flex-col shadow-2xl z-50 animate-in slide-in-from-right duration-300">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-accent" />
          <h2 className="text-xl font-bold text-foreground">Performance & Scalability</h2>
          <HelpIconButton onClick={() => setShowHelp(true)} />
        </div>
        <button onClick={onClose} className="p-2 hover:bg-cream/10 rounded transition-colors">
          <X className="w-5 h-5 text-foreground" />
        </button>
      </div>

      {/* Help Dialog */}
      {showHelp && (
        <PanelHelpDialog
          panelKey="performance"
          onClose={() => setShowHelp(false)}
        />
      )}

      {/* Toolbar */}
      <div className="p-4 border-b border-border space-y-3">
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('overview')}
            className={`px-4 py-2 rounded transition-colors ${
              viewMode === 'overview' ? 'bg-accent text-accent-foreground' : 'bg-cream/10 text-foreground hover:bg-cream/20'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setViewMode('element')}
            disabled={!selectedElementId}
            className={`px-4 py-2 rounded transition-colors ${
              viewMode === 'element' ? 'bg-accent text-accent-foreground' : 'bg-cream/10 text-foreground hover:bg-cream/20'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Element
          </button>
          <button
            onClick={handleToggleHeatmap}
            className={`ml-auto px-4 py-2 rounded transition-colors flex items-center gap-2 ${
              showHeatmap ? 'bg-accent text-accent-foreground' : 'bg-cream/10 text-foreground hover:bg-cream/20'
            }`}
          >
            <Zap className="w-4 h-4" />
            Heatmap
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Overview */}
        {viewMode === 'overview' && (
          <div className="space-y-6">
            {/* Overall Score */}
            <div className="p-6 bg-cream/5 border border-border rounded text-center">
              <div className="text-sm text-foreground-secondary mb-2">Performance Score</div>
              <div className={`text-5xl font-bold ${getScoreColor(overallScore)}`}>
                {overallScore.toFixed(0)}<span className="text-2xl">/100</span>
              </div>
            </div>

            {/* Bottlenecks */}
            {bottlenecks.length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-foreground mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Bottlenecks ({bottlenecks.length})
                </h3>
                <div className="space-y-2">
                  {bottlenecks.map(({ element, reason, severity }, index) => {
                    const severityColor =
                      severity === 'high'
                        ? 'bg-red-500/10 border-red-500/30 text-red-400'
                        : severity === 'medium'
                        ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400'
                        : 'bg-blue-500/10 border-blue-500/30 text-blue-400';

                    return (
                      <div key={index} className={`p-3 border rounded ${severityColor}`}>
                        <div className="flex items-start justify-between mb-1">
                          <div className="font-medium">{element.name}</div>
                          <span className="text-xs uppercase font-bold">{severity}</span>
                        </div>
                        <div className="text-sm opacity-90">{reason}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Missing SLOs */}
            {missingSLOs.length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-foreground mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  Missing SLOs ({missingSLOs.length})
                </h3>
                <div className="space-y-2">
                  {missingSLOs.slice(0, 5).map(({ element, missingSLOs }, index) => (
                    <div
                      key={index}
                      className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded"
                    >
                      <div className="font-medium text-foreground mb-1">{element.name}</div>
                      <div className="text-sm text-yellow-400">
                        Missing: {missingSLOs.join(', ')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Scalability Summary */}
            <div>
              <h3 className="text-lg font-bold text-foreground mb-3">Scalability</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-cream/5 border border-border rounded">
                  <div className="text-sm text-foreground-secondary">Auto-scaling</div>
                  <div className="text-2xl font-bold text-foreground mt-1">
                    {diagram.elements.filter(
                      (e) => diagram.elementMetadata[e.id]?.performance?.scalability.autoScale
                    ).length}
                  </div>
                </div>
                <div className="p-3 bg-cream/5 border border-border rounded">
                  <div className="text-sm text-foreground-secondary">Non-scalable</div>
                  <div className="text-2xl font-bold text-accent mt-1">
                    {diagram.elements.filter((e) => {
                      const perf = diagram.elementMetadata[e.id]?.performance;
                      return perf && !perf.scalability.autoScale && perf.scalability.max <= 1;
                    }).length}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={handleExportReport}
                className="flex-1 px-4 py-2 bg-cream/10 text-foreground rounded hover:bg-cream/20 flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export Report
              </button>
              <button
                onClick={handleExportCSV}
                className="flex-1 px-4 py-2 bg-cream/10 text-foreground rounded hover:bg-cream/20 flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            </div>
          </div>
        )}

        {/* Element Performance Editor */}
        {viewMode === 'element' && selectedElement && (
          <div className="space-y-4">
            <div className="p-4 bg-cream/5 border border-border rounded">
              <h3 className="font-bold text-foreground">{selectedElement.name}</h3>
              <p className="text-sm text-foreground-tertiary">
                {selectedElement.type} • {selectedElement.layer}
              </p>
            </div>

            <div className="space-y-3">
              {/* SLOs */}
              <div className="p-4 bg-cream/5 border border-border rounded space-y-3">
                <h4 className="font-medium text-foreground">Service Level Objectives (SLOs)</h4>

                <div>
                  <label className="block text-sm text-foreground-secondary mb-1">Latency</label>
                  <input
                    type="text"
                    value={elementPerf.slo.latency || ''}
                    onChange={(e) =>
                      setElementPerf({
                        ...elementPerf,
                        slo: { ...elementPerf.slo, latency: e.target.value },
                      })
                    }
                    placeholder="e.g., p99 < 100ms"
                    className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm placeholder-foreground-muted focus:outline-none focus:border-accent"
                  />
                </div>

                <div>
                  <label className="block text-sm text-foreground-secondary mb-1">Availability</label>
                  <input
                    type="text"
                    value={elementPerf.slo.availability || ''}
                    onChange={(e) =>
                      setElementPerf({
                        ...elementPerf,
                        slo: { ...elementPerf.slo, availability: e.target.value },
                      })
                    }
                    placeholder="e.g., 99.9%"
                    className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm placeholder-foreground-muted focus:outline-none focus:border-accent"
                  />
                </div>

                <div>
                  <label className="block text-sm text-foreground-secondary mb-1">Throughput</label>
                  <input
                    type="text"
                    value={elementPerf.slo.throughput || ''}
                    onChange={(e) =>
                      setElementPerf({
                        ...elementPerf,
                        slo: { ...elementPerf.slo, throughput: e.target.value },
                      })
                    }
                    placeholder="e.g., 1000 rps"
                    className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm placeholder-foreground-muted focus:outline-none focus:border-accent"
                  />
                </div>

                <div>
                  <label className="block text-sm text-foreground-secondary mb-1">Error Rate</label>
                  <input
                    type="text"
                    value={elementPerf.slo.errorRate || ''}
                    onChange={(e) =>
                      setElementPerf({
                        ...elementPerf,
                        slo: { ...elementPerf.slo, errorRate: e.target.value },
                      })
                    }
                    placeholder="e.g., < 0.1%"
                    className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm placeholder-foreground-muted focus:outline-none focus:border-accent"
                  />
                </div>
              </div>

              {/* Scalability */}
              <div className="p-4 bg-cream/5 border border-border rounded space-y-3">
                <h4 className="font-medium text-foreground">Scalability</h4>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-foreground-secondary mb-1">Min Instances</label>
                    <input
                      type="number"
                      value={elementPerf.scalability.min}
                      onChange={(e) =>
                        setElementPerf({
                          ...elementPerf,
                          scalability: {
                            ...elementPerf.scalability,
                            min: parseInt(e.target.value) || 1,
                          },
                        })
                      }
                      min="1"
                      className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-foreground-secondary mb-1">Max Instances</label>
                    <input
                      type="number"
                      value={elementPerf.scalability.max}
                      onChange={(e) =>
                        setElementPerf({
                          ...elementPerf,
                          scalability: {
                            ...elementPerf.scalability,
                            max: parseInt(e.target.value) || 10,
                          },
                        })
                      }
                      min="1"
                      className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent"
                    />
                  </div>
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={elementPerf.scalability.autoScale}
                    onChange={(e) =>
                      setElementPerf({
                        ...elementPerf,
                        scalability: {
                          ...elementPerf.scalability,
                          autoScale: e.target.checked,
                        },
                      })
                    }
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-foreground">Auto-scaling Enabled</span>
                </label>

                {elementPerf.scalability.autoScale && (
                  <div>
                    <label className="block text-sm text-foreground-secondary mb-1">Scale Metric</label>
                    <input
                      type="text"
                      value={elementPerf.scalability.scaleMetric || ''}
                      onChange={(e) =>
                        setElementPerf({
                          ...elementPerf,
                          scalability: {
                            ...elementPerf.scalability,
                            scaleMetric: e.target.value,
                          },
                        })
                      }
                      placeholder="e.g., CPU, RPS, Queue Depth"
                      className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm placeholder-foreground-muted focus:outline-none focus:border-accent"
                    />
                  </div>
                )}
              </div>

              {/* Bottleneck Flag */}
              <div className="p-4 bg-cream/5 border border-border rounded">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={elementPerf.bottleneck}
                    onChange={(e) =>
                      setElementPerf({ ...elementPerf, bottleneck: e.target.checked })
                    }
                    className="w-4 h-4"
                  />
                  <div>
                    <span className="text-sm text-foreground font-medium">Mark as Bottleneck</span>
                    <p className="text-xs text-foreground-tertiary">
                      Flag this component as a known performance bottleneck
                    </p>
                  </div>
                </label>
              </div>

              {/* Performance Score Preview */}
              <div className="p-4 bg-accent-muted border border-border rounded">
                <div className="text-sm text-foreground-secondary mb-1">Performance Score Preview</div>
                <div className={`text-3xl font-bold ${getScoreColor(calculatePerformanceScore(elementPerf))}`}>
                  {calculatePerformanceScore(elementPerf)}/100
                </div>
              </div>

              <button
                onClick={handleSave}
                className="w-full px-4 py-2 bg-accent text-accent-foreground rounded hover:bg-accent-hover font-medium"
              >
                Save Performance Settings
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
